import { takeEvery, all, select, put, call } from 'redux-saga/effects';
import {
  userSelector,
  userRoleUnderwriteManagerSelector,
  isFetchingClientTxnOppsDataSelector,
  queueTxnOppsSelector,
  isFetchingQueueTxnOppsDataSelector,
  isFetchingPropertyUWDataSelector,
  isFetchingAuctionUWDataSelector,
  signalRQueuedUpdatesSelector,
  selectedOpportunitySelector,
  routerSelector,
  exploreMapSelector,
  queueBinsStateSelector,
  selectedContainerIdSelector,
  importModuleStateSelector,
  showPostAcquisitionsSelector,
  isFetchingPostAcqWorkflowDataSelector,
  isFetchingPostAcqUWDataSelector,
  selectedPostAcqTaskSelector
} from 'store/selectors';
import {
  setPropertyUWClientTxnOpp,
  setChannelWorkflowGridDataUpdate,
  updateClientTxnOpps,
  setQueueTxnOpps,
  addSignalRQueuedUpdate,
  removeSignalRQueuedUpdates,
  updateClientTxnOppsFromQueued,
  fetchUWData,
  appNotify,
  fetchQueueTxnOppsData,
  setImportHistoryRefresh,
  updatePostAcqWorkflowTasks,
  setPostAcqWorkflowGridDataUpdate,
  setPostAcqUWClientTask,
  fetchPostAcqTaskData
} from 'store/actions';
import {
  txnOppFitsInQueue,
  postAcqTaskFitsInQueue,
  configureQueueBinsState,
} from 'sagas/getQueueTxnOppsDataSaga';
import { Constants } from 'utilities/constants';

const {
  SignalRUpdateTarget: { CLIENTTXNOPPS, QUEUETXNOPPS, PROPERTYUWTXNOPP, AUCTIONUWTXNOPPS, POSTACQWORKFLOWTASKS, POSTACQQUEUETASKS, POSTACQUWTASK },
  SignalRActionType: { DELETETXNOPP, DELETEPOSTACQUISITIONTASK, POSTACQUISITIONLOADFINISHED },
  Routes: { UNDERWRITE_PROPERTY, POSTACQ_TASK },
} = Constants;

function getCountByChannelAndUser(counts, userRoleUnderwriteManager) {
  let count = null;
  if (userRoleUnderwriteManager) {
    count = counts.uwManagerCount;
  } else {
    count = counts.notUWManagerCount;
  }
  return count;
}

function* processUpdates(updateTarget, immediateUpdate) {
  const signalRQueuedUpdates = yield select(signalRQueuedUpdatesSelector);

  function* processClientTxnOpps() {
    const userRoleUnderwriteManager = yield select(userRoleUnderwriteManagerSelector);
    const updatesArray = signalRQueuedUpdates.filter(update => update.updateTarget === CLIENTTXNOPPS);
    if (immediateUpdate) { // immediateUpdate is only passed when loading is false for the given updateTarget and we don't need to queue the update
      updatesArray.push(immediateUpdate);
    }
    const oppsToUpdate = {};
    let latestTotalCount = null;
    updatesArray.forEach(updateGroup => {
      updateGroup.data.forEach(opp => {
        oppsToUpdate[opp.id] = {
          type: updateGroup.actionType === DELETETXNOPP ? 'remove' : 'update',
          data: opp,
          actionType: updateGroup.actionType
        };
      });
      if (updateGroup.workflowGridCounts) {
        latestTotalCount = getCountByChannelAndUser(updateGroup.workflowGridCounts, userRoleUnderwriteManager);
      }
    })

    yield put(setChannelWorkflowGridDataUpdate({ data: oppsToUpdate, totalCount: latestTotalCount }));
    yield put(removeSignalRQueuedUpdates(CLIENTTXNOPPS)); // Remove the updates that were just processed
  }

  function* processQueueTxnOpps() {
    const [queueTxnOpps, user, showPostAcquisitions] = yield all([
      select(queueTxnOppsSelector, Constants.Module.UNDERWRITE),
      select(userSelector),
      select(showPostAcquisitionsSelector)
    ]);
    if (!queueTxnOpps) return;
    const updatesArray = signalRQueuedUpdates.filter(update => update.updateTarget === QUEUETXNOPPS);
    if (immediateUpdate) { // immediateUpdate is only passed when loading is false for the given updateTarget and we don't need to queue the update
      updatesArray.push(immediateUpdate);
    }
    let foundUpdatedOpps = false; // Only update clientTxnOpps data if the rows to update currently exist.
    const newQueueTxnOpps = [...queueTxnOpps];
    updatesArray.forEach(updateGroup => {
      updateGroup.data.forEach(opp => {
        const fitsQueue = txnOppFitsInQueue(opp, user.id, showPostAcquisitions)
        const oppToUpdateIndex = newQueueTxnOpps.findIndex(x => x.id === opp.id);
        if (fitsQueue && updateGroup.actionType !== DELETETXNOPP) {
          if (oppToUpdateIndex > -1) { // If opp exists in queue,
            foundUpdatedOpps = true;
            newQueueTxnOpps[oppToUpdateIndex] = opp; // Update
          } else { // Else add
            foundUpdatedOpps = true;
            newQueueTxnOpps.push(opp);
          }
        } else { // If opp doesn't belong in queue, or action is delete
          if (oppToUpdateIndex > -1) { // Delete if it exists in queue
            foundUpdatedOpps = true;
            newQueueTxnOpps.splice(oppToUpdateIndex, 1);
          }
        }
      })
    })

    if (foundUpdatedOpps) {
      yield put(setQueueTxnOpps(Constants.Module.UNDERWRITE, newQueueTxnOpps));

      const queueBinsState = yield select(queueBinsStateSelector, Constants.Module.UNDERWRITE);
      const newQueueBinsState = yield call(configureQueueBinsState, Constants.Module.UNDERWRITE, newQueueTxnOpps, showPostAcquisitions);
      const areSame = queueBinsState.allTasks.areSame(newQueueBinsState.allTasks);

      if (!areSame) {
        yield put(fetchQueueTxnOppsData(Constants.Module.UNDERWRITE));
      }
    }
    yield put(removeSignalRQueuedUpdates(QUEUETXNOPPS)); // Remove the updates that were just processed
  }

  function* processPropertyUWTxnOpp() {
    const selectedOpportunity = yield select(selectedOpportunitySelector);
    if (!selectedOpportunity) return;

    const updatesArray = signalRQueuedUpdates.filter(
      update => update.updateTarget === PROPERTYUWTXNOPP
    );
    if (immediateUpdate) { // immediateUpdate is only passed when loading is false for the given updateTarget and we don't need to queue the update
      updatesArray.push(immediateUpdate);
    }

    let newPropertyUWTxnOpp = null;
    updatesArray.forEach(updateGroup => {
      updateGroup.data.forEach(opp => {
        if (opp.id === selectedOpportunity.id) {
          newPropertyUWTxnOpp = opp;
        }
      })
    })

    if (newPropertyUWTxnOpp) {
      yield put(setPropertyUWClientTxnOpp(newPropertyUWTxnOpp));
    }
    yield put(removeSignalRQueuedUpdates(PROPERTYUWTXNOPP)); // Remove the updates that were just processed
  }

  function* processPostAcqWorkflowTasks() {
    const updatesArray = signalRQueuedUpdates.filter(update => update.updateTarget === POSTACQWORKFLOWTASKS);
    if (immediateUpdate) { // immediateUpdate is only passed when loading is false for the given updateTarget and we don't need to queue the update
      updatesArray.push(immediateUpdate);
    }
    const tasksToUpdate = {};
    let latestTotalCount = null;
    updatesArray.forEach(updateGroup => {
      updateGroup.data.forEach(task => {
        tasksToUpdate[task.id] = {
          type: updateGroup.actionType === DELETEPOSTACQUISITIONTASK ? 'remove' : 'update',
          data: task,
          actionType: updateGroup.actionType
        };
      });
      if (updateGroup.workflowGridCounts) {
        latestTotalCount = getCountByChannelAndUser(updateGroup.workflowGridCounts); // TODO: refactor if manager role not needed
      }
    })

    yield put(setPostAcqWorkflowGridDataUpdate({ data: tasksToUpdate, totalCount: latestTotalCount }));
    yield put(removeSignalRQueuedUpdates(POSTACQWORKFLOWTASKS)); // Remove the updates that were just processed
  }

  function* processPostAcqQueueTasks() {
    const [queueTasks, user] = yield all([
      select(queueTxnOppsSelector, Constants.Module.POSTACQ),
      select(userSelector)
    ]);
    if (!queueTasks) return;
    const updatesArray = signalRQueuedUpdates.filter(update => update.updateTarget === POSTACQQUEUETASKS);
    if (immediateUpdate) { // immediateUpdate is only passed when loading is false for the given updateTarget and we don't need to queue the update
      updatesArray.push(immediateUpdate);
    }
    let foundUpdatedTasks = false; // Only update tasks data if the rows to update currently exist.
    const newQueueTasks = [...queueTasks];
    updatesArray.forEach(updateGroup => {
      updateGroup.data.forEach(task => {
        const fitsQueue = postAcqTaskFitsInQueue(task, user.id, true)
        const taskToUpdateIndex = newQueueTasks.findIndex(x => x.id === task.id);
        if (fitsQueue && updateGroup.actionType !== DELETEPOSTACQUISITIONTASK) {
          if (taskToUpdateIndex > -1) { // If task exists in queue,
            foundUpdatedTasks = true;
            newQueueTasks[taskToUpdateIndex] = task; // Update
          } else { // Else add
            foundUpdatedTasks = true;
            newQueueTasks.push(task);
          }
        } else { // If task doesn't belong in queue, or action is delete
          if (taskToUpdateIndex > -1) { // Delete if it exists in queue
            foundUpdatedTasks = true;
            newQueueTasks.splice(taskToUpdateIndex, 1);
          }
        }
      })
    })

    if (foundUpdatedTasks) {
      yield put(setQueueTxnOpps(Constants.Module.POSTACQ, newQueueTasks));

      const queueBinsState = yield select(queueBinsStateSelector, Constants.Module.POSTACQ);
      const newQueueBinsState = yield call(configureQueueBinsState, Constants.Module.POSTACQ, newQueueTasks);
      const areSame = queueBinsState.allTasks.areSame(newQueueBinsState.allTasks);

      if (!areSame) {
        yield put(fetchQueueTxnOppsData(Constants.Module.POSTACQ));
      }
    }
    yield put(removeSignalRQueuedUpdates(POSTACQQUEUETASKS)); // Remove the updates that were just processed
  }

  function* processPostAcqUWTask() {
    const selectedTask = yield select(selectedPostAcqTaskSelector);
    if (!selectedTask) return;

    const updatesArray = signalRQueuedUpdates.filter(
      update => update.updateTarget === POSTACQUWTASK
    );
    if (immediateUpdate) { // immediateUpdate is only passed when loading is false for the given updateTarget and we don't need to queue the update
      updatesArray.push(immediateUpdate);
    }

    let newPostAcqUWTask = null;
    updatesArray.forEach(updateGroup => {
      updateGroup.data.forEach(opp => {
        if (opp.id === selectedTask.id) {
          newPostAcqUWTask = opp;
        }
      })
    })

    if (newPostAcqUWTask) {
      yield put(setPostAcqUWClientTask(newPostAcqUWTask));
    }
    yield put(removeSignalRQueuedUpdates(POSTACQUWTASK)); // Remove the updates that were just processed
  }

  switch (updateTarget) {
    case CLIENTTXNOPPS:
      yield call(processClientTxnOpps);
      break;
    case QUEUETXNOPPS:
      yield call(processQueueTxnOpps);
      break;
    case PROPERTYUWTXNOPP:
      yield call(processPropertyUWTxnOpp);
      break;
    case POSTACQWORKFLOWTASKS:
      yield call(processPostAcqWorkflowTasks);
      break;
    case POSTACQQUEUETASKS:
      yield call(processPostAcqQueueTasks);
      break;
    case POSTACQUWTASK:
      yield call(processPostAcqUWTask);
      break;
    default:
      break;
  }
}

export function* clientTxnOppsUpdateSaga() {
  yield takeEvery(updateClientTxnOpps, function* ({ payload }) {
    const { txnOpps, actionResult } = payload;
    const [user, isFetchingClientTxnOppsData, isFetchingQueueTxnOppsData, isFetchingPropertyUWData, isFetchingAuctionUWData] = yield all([
      select(userSelector),
      select(isFetchingClientTxnOppsDataSelector),
      select(isFetchingQueueTxnOppsDataSelector, Constants.Module.UNDERWRITE),
      select(isFetchingPropertyUWDataSelector),
      select(isFetchingAuctionUWDataSelector)
    ]);

    if (actionResult?.needRefreshPricing) { // This flag indicates an update after a response is received from Refresh Opp request
      const [selectedOpportunity, routerState, exploreMapState] = yield all([
        select(selectedOpportunitySelector),
        select(routerSelector),
        select(exploreMapSelector)
      ]);

      if (user.id === actionResult.actionInitializer) { // Notify the user who initiated the refresh of success/failure
        yield put(appNotify(actionResult.message, actionResult.success ? 'success' : 'error'));
      }

      const updateContainsSelectedOpportunity = selectedOpportunity != null && txnOpps.find(opp => opp.id === selectedOpportunity.id);
      const isOnPropUWOrExploreModal = routerState.location.pathname.includes(UNDERWRITE_PROPERTY) || exploreMapState.viewUnderwriteModalIsOpen;
      if (updateContainsSelectedOpportunity && isOnPropUWOrExploreModal) {
        const selectedContainerId = yield select(selectedContainerIdSelector);
        yield put(fetchUWData(selectedOpportunity.id, selectedContainerId));
      }
    }

    function* triggerImportOppHistGridRefresh() {
      const importModule = Constants.Module.UNDERWRITE;
      const importModuleState = yield select(importModuleStateSelector, importModule);
      const importRefresh = (importModuleState.importHistoryRefresh ?? 0) + 1;
      yield put(setImportHistoryRefresh({ module: importModule, value: importRefresh }));
    }
    
    function* handleUpdate(updateTarget, loadingFlag) {
      const updateObj = {
        updateTarget,
        data: txnOpps,
        actionType: actionResult?.actionType,
        workflowGridCounts: actionResult?.workflowGridCounts
      }
      if (loadingFlag) {
        yield put(addSignalRQueuedUpdate(updateObj));
      } else {
        yield call(processUpdates, updateTarget, updateObj);
      }
    }

    if (actionResult?.actionType === DELETETXNOPP) { // For deletes,
      yield call(handleUpdate, QUEUETXNOPPS, isFetchingQueueTxnOppsData); // Run deletion from queue for all users
      yield call(triggerImportOppHistGridRefresh);
      if (actionResult.actionInitializer !== user.id) return; // Only run for workflow grid for the user that initiated the delete
      yield call(handleUpdate, CLIENTTXNOPPS, isFetchingClientTxnOppsData);
    } else {
      // For each txnOpp type, create an update object and, depending on each loading state, either add to queued updates or process immediately
      yield call(handleUpdate, CLIENTTXNOPPS, isFetchingClientTxnOppsData);
      yield call(handleUpdate, QUEUETXNOPPS, isFetchingQueueTxnOppsData);
      yield call(handleUpdate, PROPERTYUWTXNOPP, isFetchingPropertyUWData);
      yield call(handleUpdate, AUCTIONUWTXNOPPS, isFetchingAuctionUWData);
    }
  });

  yield takeEvery(updatePostAcqWorkflowTasks, function* ({ payload }) {
    const { tasks, actionResult } = payload;
    const [user, isFetchingPostAcqWorkflowData, isFetchingQueueTasksData, isFetchingPostAcqUWData] = yield all([
      select(userSelector),
      select(isFetchingPostAcqWorkflowDataSelector),
      select(isFetchingQueueTxnOppsDataSelector, Constants.Module.UNDERWRITE),
      select(isFetchingPostAcqUWDataSelector)
    ]);

    if (actionResult?.actionType === POSTACQUISITIONLOADFINISHED) { // This flag indicates an update after a response is received from Load Task request
      console.log(actionResult)
      const [selectedTask, routerState, exploreMapState] = yield all([
        select(selectedPostAcqTaskSelector),
        select(routerSelector),
        select(exploreMapSelector)
      ]);

      if (user.id === actionResult.actionInitializer) { // Notify the user who initiated the refresh of success/failure
        yield put(appNotify(actionResult.message, actionResult.success ? 'success' : 'error'));
      }

      const updateContainsSelectedTask = selectedTask != null && tasks.find(task => task.id === selectedTask.id);
      const isOnPostAcqUWOrExploreModal = routerState.location.pathname.startsWith(POSTACQ_TASK) || exploreMapState.viewUnderwriteModalIsOpen;
      if (updateContainsSelectedTask && isOnPostAcqUWOrExploreModal) {
        yield put(fetchPostAcqTaskData(selectedTask.id));
      }
    }
    
    function* handleUpdate(updateTarget, loadingFlag) {
      const updateObj = {
        updateTarget,
        data: tasks,
        actionType: actionResult?.actionType,
        workflowGridCounts: actionResult?.workflowGridCounts
      }
      if (loadingFlag) {
        yield put(addSignalRQueuedUpdate(updateObj));
      } else {
        yield call(processUpdates, updateTarget, updateObj);
      }
    }

    if (actionResult?.actionType === DELETEPOSTACQUISITIONTASK) { // For deletes,
      // yield call(triggerImportOppHistGridRefresh);
      if (actionResult.actionInitializer !== user.id) return; // Only run for workflow grid for the user that initiated the delete
      yield call(handleUpdate, POSTACQWORKFLOWTASKS, isFetchingPostAcqWorkflowData);
      yield call(handleUpdate, POSTACQQUEUETASKS, isFetchingQueueTasksData);
    } else {
      // For each txnOpp type, create an update object and, depending on each loading state, either add to queued updates or process immediately
      yield call(handleUpdate, POSTACQWORKFLOWTASKS, isFetchingPostAcqWorkflowData);
      yield call(handleUpdate, POSTACQQUEUETASKS, isFetchingQueueTasksData);
      yield call(handleUpdate, POSTACQUWTASK, isFetchingPostAcqUWData);
    }
  });

  yield takeEvery(updateClientTxnOppsFromQueued, function* ({ payload: updateTarget = CLIENTTXNOPPS }) {
    yield call(processUpdates, updateTarget);
  });
}
